import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Icon } from "@phosphor-icons/react";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { cn } from "@/zapi-frontend/shared/lib/utils";

export const FILE_ROW_ACTION =
  "relative z-10 rounded-full bg-background/80 text-foreground opacity-0 shadow-xs transition-opacity group-focus-within/file:opacity-100 group-hover/file:opacity-100 hover:bg-background focus-visible:opacity-100 data-popup-open:opacity-100 pointer-coarse:opacity-100";

export function ContextPage({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="relative w-full min-w-0" data-testid={testId}>
      <div className="flex flex-col gap-6 pb-8">{children}</div>
    </div>
  );
}

export function ContextPageHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex min-h-10 flex-wrap items-center justify-between gap-3">
      <h1 className="typo-h3 text-foreground">{title}</h1>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ContextEmpty({
  icon: EmptyIcon,
  title,
  description
}: {
  icon: Icon;
  title?: string;
  description?: string;
}) {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border bg-card/60 px-12 py-14 text-center text-balance">
      <div className="flex max-w-sm flex-col items-center gap-2">
        <span className="mb-2 flex size-10 items-center justify-center rounded-lg bg-muted text-foreground">
          <EmptyIcon weight="duotone" className="size-6" />
        </span>
        {title && <div className="font-heading text-lg font-medium tracking-tight text-foreground">{title}</div>}
        {description && <p className="text-sm/relaxed text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

export function FileList({ children }: { children: ReactNode }) {
  return (
    <div role="list" className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
      {children}
    </div>
  );
}

export function FileRow({
  glyph: Glyph,
  pending = false,
  title,
  meta,
  format,
  actions,
  testId
}: {
  glyph: Icon;
  pending?: boolean;
  title: ReactNode;
  meta?: ReactNode;
  format?: ReactNode;
  actions?: ReactNode;
  testId?: string;
}) {
  const { t } = useTranslation("common");
  return (
    <div
      role="listitem"
      data-testid={testId}
      className="group/file relative isolate flex min-h-14 items-center gap-3 border-t border-foreground/5 px-5 py-3 transition-colors first:border-t-0 hover:bg-muted/60"
    >
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-lg bg-card text-greyscale-500 ring-1 ring-foreground/5",
          pending && "animate-pulse"
        )}
      >
        {pending ? (
          <Spinner aria-label={t("loading")} className="size-5 text-muted-foreground" />
        ) : (
          <Glyph weight="duotone" className="size-5" aria-hidden="true" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="typo-label-md truncate text-foreground">{title}</span>
        {meta && <span className="typo-caption flex min-w-0 items-center gap-1 text-muted-foreground">{meta}</span>}
      </div>
      {format && <span className="typo-caption hidden w-24 shrink-0 text-muted-foreground sm:block">{format}</span>}
      {actions && <div className="relative z-10 shrink-0">{actions}</div>}
    </div>
  );
}
